import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { partnerApi } from '../../api/partner.ts';
import { Alert, LoadingBlock, Select } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { money, periodLabel, Pill, recentPeriods, shortDate, TableShell } from './common.tsx';

const PERIODS = recentPeriods(12);

/** A reversal is money going the other way, and should read like it. */
function toneFor(type: string): 'good' | 'warn' | 'bad' | 'neutral' {
  if (type.endsWith('_reversal')) return 'bad';
  if (type === 'commission_earned' || type === 'bonus') return 'good';
  if (type === 'payout') return 'neutral';
  return 'warn';
}

export default function PartnerCommissionsPage() {
  useDocumentTitle('Partner commissions');
  const [period, setPeriod] = useState(PERIODS[0]!);

  const commissions = useQuery({
    queryKey: ['partner', 'commissions', period],
    queryFn: () => partnerApi.commissions(period),
  });

  const entries = commissions.data?.entries ?? [];
  // The month's total comes from the server, not from this array: the listing
  // stops at a row cap, and a net added up from a truncated list is wrong for
  // exactly the partners with the most entries.
  const net = commissions.data?.netCents ?? 0;
  const currency = commissions.data?.currency;

  return (
    <div className="portal-page">
      <WorkPage
        title="Commissions"
        description="Every entry against your account for one month, including the reversals a refund or a chargeback produces."
        actions={
          <Select
            label="Month"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            options={PERIODS.map((value) => ({ value, label: periodLabel(value) }))}
          />
        }
      >
        {commissions.isLoading ? <LoadingBlock label="Loading your commission entries…" /> : null}
        {commissions.isError ? (
          <Alert tone="error" title="Those entries could not be loaded">
            {commissions.error instanceof Error ? commissions.error.message : 'Please try again in a moment.'}
          </Alert>
        ) : null}
        {commissions.data && entries.length === 0 ? (
          <WorkEmpty title={`Nothing recorded in ${periodLabel(period)}`}>
            Commission is recorded when an attributed customer is charged.
          </WorkEmpty>
        ) : null}
        {entries.length > 0 ? (
          <>
            <TableShell
              label="Commissions"
              head={
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Entry
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Date
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-semibold">
                    Eligible revenue
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-semibold">
                    Rate
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-semibold">
                    Amount
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    State
                  </th>
                </tr>
              }
            >
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                  <td className="px-4 py-3">
                    <Pill value={entry.type} tone={toneFor(entry.type)} />
                    {entry.reason ? (
                      <div className="table-text mt-1 max-w-64 text-xs text-[color:var(--color-muted)]">
                        {entry.reason}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">{shortDate(entry.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    {entry.eligibleRevenueCents ? money(entry.eligibleRevenueCents, entry.currency) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">{entry.ratePercent === null ? '—' : `${entry.ratePercent}%`}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(entry.amountCents, entry.currency)}</td>
                  <td className="px-4 py-3">
                    {/* The distinction that decides whether a number can still
                      change: an entry with no finalization stamp is an
                      estimate a refund can still reverse.

                      Deliberately NOT a "paid" flag. `payoutId` says which
                      batch a row belongs to, and only the payout rows ever
                      carry one — before PayPal has been contacted, and still
                      after a refused send. Whether money arrived is the
                      payout's own status, on the Payouts screen. */}
                    {entry.finalized ? <Pill value="final" tone="neutral" /> : <Pill value="estimate" tone="warn" />}
                    {entry.payoutId ? (
                      <div className="mt-1">
                        <Pill value="in a payout" tone="good" />
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </TableShell>
            <p className="mt-3 text-right text-sm text-[color:var(--color-muted)]">
              {commissions.data?.truncated ? (
                <>
                  Showing the {entries.length} most recent of {commissions.data.entryCount} entries.{' '}
                </>
              ) : null}
              {/* Named precisely, because the table above it also lists the
                payout rows this total excludes: earning $20 and being paid
                $20 in one month makes the visible rows sum to nothing under
                a net of $20, and only the label can explain that. */}
              Net commission for {periodLabel(period)}, before payouts:{' '}
              <span className="font-semibold text-[color:var(--color-ink)]">{money(net, currency)}</span>
            </p>
          </>
        ) : null}
      </WorkPage>
    </div>
  );
}
